import {
  Suspense,
  createContext,
  useMemo,
  useReducer,
  useState,
} from "react";
import { Card, Col, Row, Table, Button, Form, Modal } from "react-bootstrap";
import { ArchiveOutlined }   from '@mui/icons-material';
import { IconButton, Tooltip } from "@mui/material";
import {
  CREATE,
  NEW_PATIENT,
  REVIEW_SELECTED_PATIENTS,
  KEY_DATES,
  STATUS,
  PATIENT,
  PROGRAM,
  REVIEW_ALL,
  DIAGNOSIS,
  LAST_REVIEW,
  STARTED,
  WEEK,
  GESTATION_DIABETES,
  CURRENT_GESTATION,
  LINK,
  DUE_DATE,
  MANAGEMENT_TYPE,
  TEAM,
  DefaultBreakfastStart,
  DefaultBreakfastEnd,
  DefaultFastingStart,
  DefaultFastingEnd,
  DefaultOvernightStart,
  DefaultOvernightEnd,
  DefaultLunchStart,
  DefaultLunchEnd,
  DefaultDinnerStart,
  DefaultDinnerEnd,
  ALERT,
  SYSTOLIC,
  HIGH,
  POST_MEAL,
  FASTING,
  LOW,
  NO_DATA,
  DIAGNOSIS_DATE,
  VIEW_LINK,
  PATIENT_ARCHIVE,
  DATE
} from "../../util/const";
import {
  CalculateCurrentGestation,
  CalculateDiagnosisDate,
  LastReviewedDate,
} from "../../util/utils";
import { MedicationRegime, MedicationRegimeGroup, Patient } from "../../types/patient";
import CreatePatient from "../CreatePatient";
import PatientReview from "../ReviewPatient";
import PatientForm from "../PatientForm";
import { Review } from "../../types/review";
import LoadingSpinner from "../../Components/LoadingSpinner";
import LinkEdit from "../LinkEdit";
import LinkAdd from "../LinkAdd";
import { FormatDateForDisplay, GetCurrentDate } from "../../util/data-utils";
import ResourceCheckbox from "../../Components/ResourceCheckbox";
import { DatabaseManager } from "../../services/DatabaseManager";
import { PatientProps } from "../PatientList";
import PatientDisplay from "../../Components/PatientDisplay";


export type HomePageProps = {
  toggleDisplay: (didSave: boolean) => void;
};

export type ReviewProps = {
  toggleDisplay: (didComplete: boolean) => void;
  patients: Patient[];
};

export const DefaultPatient = () => {
  return {
    documentId: "",
    resourceId: "",
    studyId: "",
    urn: "",
    salutation: "",
    givenNames: [] as string[],
    familyName: "",
    birthDate: "",
    birthPlace: "",
    email: "",
    phone: "",
    weight: "",
    height: "",
    diabetesType: "GDM",
    diabetesGestation: "",
    currentGestation: "",
    dueDate: "",
    managementType: [] as string[],
    team: "Orange",
    group: "",
    medicationNames: [] as string[],
    medicationRegimeGroup: {
      encounterType: "",
      recordedOn: "",
      regimes: [] as MedicationRegime []
    } as MedicationRegimeGroup,
    indigenousStatus: "",
    language: "",
    ethnicity: "",
    ogttFasting: "",
    ogttOneHour: "",
    ogttTwoHour: "",
    fastingStart: DefaultFastingStart,
    fastingEnd: DefaultFastingEnd,
    breakfastStart: DefaultBreakfastStart,
    breakfastEnd: DefaultBreakfastEnd,
    lunchStart: DefaultLunchStart,
    lunchEnd: DefaultLunchEnd,
    dinnerStart: DefaultDinnerStart,
    dinnerEnd: DefaultDinnerEnd,
    overnightStart: DefaultOvernightStart,
    overnightEnd: DefaultOvernightEnd,
    overrideDefaults: false,
    currentReview: {
      reviewDate: "",
      message: "",
      note: "",
    } as Review,
    amHigh: false,
    fastingHigh: false,
    bglLow: false,
    sysHigh: false,
    alertNumber: 0,
    noData: false,
    archivedReason: "",
    archivedOn: "",
    randomisedDate: ""
  } as Patient;
};

export const PatientContext = createContext({
  patient: DefaultPatient(),
});

const ActivePatients = (props: PatientProps) => {

  // These state variables controls what form is rendered on screen
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [isEditLinking, setIsEditLinking] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);

  // Hold the currently selected patient and provide a context so it can
  // be accessed in other parts of the app
  const [patient, setPatient] = useState(DefaultPatient());
  const patientContextValue = useMemo(() => ({ patient }), [patient]);

  const reviewState = { reviewAll: true, reviewingPatients: props.patients }
  const enum REDUCER_ACTION_TYPE {
    TOGGLE_REVIEW_ALL,
    TOGGLE_REVIEW_PATIENT,
    PATIENT_CREATED,
    PATIENT_ARCHIVED,
    SHOW_ARCHIVE_ERROR,
  }
  type ReducerAction = {
    type: REDUCER_ACTION_TYPE.TOGGLE_REVIEW_PATIENT | REDUCER_ACTION_TYPE.PATIENT_CREATED | REDUCER_ACTION_TYPE.PATIENT_ARCHIVED,
    payload: Patient[]
  }
  type ReviewAllReducerAction = {
    type: REDUCER_ACTION_TYPE.TOGGLE_REVIEW_ALL,
  }
  type ArchiveReducerAction = {
    type: REDUCER_ACTION_TYPE.SHOW_ARCHIVE_ERROR,
    payload: boolean
  }
  const reducer = (state: typeof reviewState, action: ReducerAction | ArchiveReducerAction | ReviewAllReducerAction): typeof 
  reviewState => {
    switch(action.type)
    {
      case REDUCER_ACTION_TYPE.TOGGLE_REVIEW_ALL: {
        if(state.reviewAll) return  {...state, reviewAll: false, reviewingPatients: []}
        else return {...state, reviewAll: true, reviewingPatients: props.patients}
      }        
      case REDUCER_ACTION_TYPE.TOGGLE_REVIEW_PATIENT: {
        return {...state, reviewAll: action.payload.length === props.patients.length, reviewingPatients: action.payload}
      }
      case REDUCER_ACTION_TYPE.PATIENT_CREATED: {
        return {...state, reviewAll: true, reviewingPatients: action.payload}
      }
      case REDUCER_ACTION_TYPE.PATIENT_ARCHIVED: {
        return {...state, reviewAll: true, reviewingPatients: action.payload}
      }
      default: return state;
    }
  }
  const [review, dispatch] = useReducer(reducer, reviewState); 


  /// filtering & searching
  const [diagnosisType, setDiagnosisType] = useState("");
  const [managementType, setManagementType] = useState("");
  const [team, setTeam] = useState("");
  const [search, setSearch] = useState("");
  const [currentState, setCurrentState] = useState("down");
  
  /// archiving
  const today = GetCurrentDate()
  const [archivePatient, setArchivePatient] = useState<Patient | undefined>();
  const [archiveOn, setArchiveOn] = useState(today);
  const [isInvalid, setIsInvalid] = useState(false);


  const onSearchInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
  };

  const onLinkClicked = (p: Patient, e: React.MouseEvent<HTMLButtonElement>) => {
    /// replace null values with empty strings
    /// so the controlled variables don't error
    if (p.medicationRegimeGroup === null || p.medicationRegimeGroup === undefined) p.medicationRegimeGroup = {} as MedicationRegimeGroup;

    p = JSON.parse(JSON.stringify(p), (key, value) => {
      return value === null || value === undefined ? "" : value;
    });

    setPatient(p);
    toggleLinking(false);
  };

  const onLinkedClicked = (p: Patient, e: React.MouseEvent<HTMLButtonElement>) => {

    if (p.medicationRegimeGroup === null || p.medicationRegimeGroup === undefined) p.medicationRegimeGroup = {} as MedicationRegimeGroup;
    
    /// replace null values with empty strings
    /// so the controlled variables don't error
    p = JSON.parse(JSON.stringify(p), (key, value) => {
      return value === null || value === undefined ? "" : value;
    });

    setPatient(p);
    toggleEditLinking(false);
  };

  const onCreateClicked = () => {
    toggleCreating(false);
  };

  const toggleCreating = (didSave: boolean) => {
    if (isCreating && didSave) {
      props.refreshData(false).then(
        (value) => {
          dispatch({type: REDUCER_ACTION_TYPE.PATIENT_CREATED, payload: value})
        }
      );
    }
    setIsCreating(!isCreating);
  };

  const toggleEditing = (didSave: boolean) => {
    if (isEditing && didSave) props.refreshData(false);
    setIsEditing(!isEditing);
  };

  const toggleLinking = (didSave: boolean) => {
    if (isLinking && didSave) props.refreshData(false);
    setIsLinking(!isLinking);
  };

  const toggleEditLinking = (didSave: boolean) => {
    if (isEditLinking && didSave) props.refreshData(false);
    setIsEditLinking(!isEditLinking);
  };

  const onRowSelected = (p: Patient, e: React.MouseEvent<HTMLTableRowElement>) => {

    // Prevent row selection when checkbox or button or svg is clicked in table row
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLButtonElement || e.target instanceof SVGPathElement || e.target instanceof SVGSVGElement ) {
      e.stopPropagation();
      return;
    }

    if (p.currentReview === null || p.currentReview === undefined) p.currentReview = {} as Review;
    if (p.medicationRegimeGroup === null || p.medicationRegimeGroup === undefined) p.medicationRegimeGroup = {} as MedicationRegimeGroup;

    /// replace null values with empty strings
    /// so the controlled variables don't error
    p = JSON.parse(JSON.stringify(p), (key, value) => {
      return value === null || value === undefined ? "" : value;
    });

    setPatient(p);
    toggleEditing(false);
  };

  const toggleReviewing = (didComplete: boolean) => {
    if (isReviewing && didComplete) props.refreshData(false);
    setIsReviewing(!isReviewing);
  };

  const toggleReviewAll = () => {
    dispatch({type: REDUCER_ACTION_TYPE.TOGGLE_REVIEW_ALL})
  };

  const toggleReviewPatient = (patient: Patient, checked: boolean) => {
    let temp = review.reviewingPatients; //reviewPatients.current;
    if (checked) {
      const index = temp.indexOf(patient);
      if (index > -1) temp = temp.filter((reviewPatient: Patient) => reviewPatient !== patient);
    } else {
      temp.push(patient);
      temp.sort((a: Patient, b: Patient) => a.resourceId.localeCompare(b.resourceId));
    }
    dispatch({type: REDUCER_ACTION_TYPE.TOGGLE_REVIEW_PATIENT, payload: temp})
  };

  const onArchiveConfirmClicked = async () => {

    if(archivePatient === undefined)
      return;

    if(archivePatient.archivedReason == null || archivePatient.archivedReason === undefined || archivePatient.archivedReason.length === 0)
    {
      setIsInvalid(true);
      return;
    }

    archivePatient!.isActive = false;
    await DatabaseManager.getInstance().put(PATIENT_ARCHIVE, archivePatient!)
    .then((response) => {
      props.refreshData(true).then( 
        (value) => {
          dispatch({type: REDUCER_ACTION_TYPE.PATIENT_ARCHIVED, payload: value})
          setArchivePatient(undefined);
        }
      );
    })
    .catch((error) => {
      console.error(error);
    });  
  }

  const onArchiveSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if(archivePatient === undefined)
      return;

    const reason = e.target.value;
    if(reason !== '')
    {
      archivePatient.archivedReason = reason;
    }
    setIsInvalid(reason === '');
  }

  // const getProgramWeek = (startDate: string | undefined) => {
  //   if (startDate === undefined || startDate === null || startDate.length === 0) {
  //     return "-";
  //   }
  //   var today = new Date().getTime();
  //   var started = new Date(startDate).getTime();
  //   const diffInMs = today - started;
  //   const aDayInMs = 24 * 60 * 60 * 1000;
  //   var daysDiff = Math.round(diffInMs / aDayInMs);
  //   daysDiff++;
  //   var weeks = Math.floor(daysDiff / 7);
  //   var partWeeks = daysDiff % 7;
  //   if (partWeeks > 0) weeks++;
  //   return weeks;
  // };

  const getStatus = (p: Patient) => {
    if (p.documentId === null) {
      return (
        <Button type="button" onClick={(e) => onLinkClicked(p, e)} className="fs-6 btn-color">
          {LINK}
        </Button>
      );
    }
    return (
      <Button type="button" onClick={(e) => onLinkedClicked(p, e)} className="fs-6 btn-color">
        {VIEW_LINK}
      </Button>
    ); 
  };

  const onSortChange = () => {
    if (currentState === "down") setCurrentState("up");
    else if (currentState === "up") setCurrentState("down");
  };

  const sortTypes: any = {
    up: {
      class: "sort-up",
      fn: (a: Patient, b: Patient) => {
        if (Number(a.alertNumber) === Number(b.alertNumber)) {
          return +new Date(a.dueDate) - +new Date(b.dueDate);
        }
        return Number(a.alertNumber) - Number(b.alertNumber);
      },
    },
    down: {
      class: "sort-down",
      fn: (a: Patient, b: Patient) => {
        if (Number(a.alertNumber) === Number(b.alertNumber)) {
          return +new Date(a.dueDate) - +new Date(b.dueDate);
        }
        return Number(b.alertNumber) - Number(a.alertNumber);
      },
    },
  };

  const showAlerts = (aPatient: Patient) => {
    return aPatient.group === "Intervention" && aPatient.documentId !== null && aPatient.documentId.length > 0;
  }

  const alertRed = () => {
    return <span className="alert-red">●</span>;
  }

  const alertOrange = () => {
    return <span className="alert-orange">●</span>
  }

  const alertGreen = () => {
    return <span className="alert-green">●</span>
  }

  if (isCreating) {
    return (
      <PatientContext.Provider value={patientContextValue}>
        <CreatePatient toggleDisplay={toggleCreating} />
      </PatientContext.Provider>
    );
  }

  if (isEditing) {
    return (
      <PatientContext.Provider value={patientContextValue}>
        <PatientForm toggleDisplay={toggleEditing} isEditing={true} />
      </PatientContext.Provider>
    );
  }

  if (isLinking) {
    return (
      <PatientContext.Provider value={patientContextValue}>
        <Suspense fallback={<LoadingSpinner />}>
          <LinkAdd toggleDisplay={toggleLinking} />
        </Suspense>
      </PatientContext.Provider>
    );
  }

  if (isEditLinking) {
    return (
      <PatientContext.Provider value={patientContextValue}>
        <Suspense fallback={<LoadingSpinner />}>
          <LinkEdit toggleDisplay={toggleEditLinking} />
        </Suspense>
      </PatientContext.Provider>
    );
  }

  if (isReviewing) {
    return (
      <PatientReview toggleDisplay={toggleReviewing} patients={review.reviewingPatients} />
    );
  }

  return (
    <>
      <Card className="mt-3 mb-3" border="secondary" text="white">
        <Card.Header className="fs-5 app-colour">
          <Row>
            <Col className="right">
              <Button
                type="button"
                onClick={(e) => {
                  toggleReviewing(false);
                }}
                className="fs-6 btn-outline-white me-2"
                disabled={review.reviewingPatients.length === 0}>
                {REVIEW_SELECTED_PATIENTS}
              </Button>
              <Button type="button" onClick={onCreateClicked} className="fs-6 btn-outline-white">
                {CREATE} {NEW_PATIENT}
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {props.patients && props.patients.length > 0 ? (
            <Table striped bordered hover>
              <thead className="table-header">
                <tr>
                  <th colSpan={9}>
                    <Form>
                      <Row>
                        <Col />
                        <Form.Group as={Col} controlId="team">
                          <Form.Select value={team} onChange={(e) => setTeam(e.target.value)}>
                            <option value="">Any Team</option>
                            <option value="Orange">Orange</option>
                            <option value="Yellow">Yellow</option>
                            <option value="Blue">Blue</option>
                            <option value="Purple">Purple</option>
                            <option value="Diamond">Diamond</option>
                          </Form.Select>
                        </Form.Group>
                        <Form.Group as={Col} controlId="managementType">
                          <Form.Select
                            value={managementType}
                            onChange={(e) => setManagementType(e.target.value)}>
                            <option value="">Any Management Type</option>
                            <option value="Diet">Diet Only</option>
                            <option value="Metformin">Metformin Only</option>
                            <option value="Insulin">Insulin Only</option>
                            <option value="Metformin and Insulin">Metformin and Insulin</option>
                          </Form.Select>
                        </Form.Group>
                        <Form.Group as={Col} controlId="diagnosisType">
                          <Form.Select
                            value={diagnosisType}
                            onChange={(e) => setDiagnosisType(e.target.value)}>
                            <option value="">Any Diagnosis Type</option>
                            <option value="GDM">GDM</option>
                            <option value="T2DM">T2DM</option>
                            <option value="T1DM">T1DM</option>
                          </Form.Select>
                        </Form.Group>
                        <Form.Group as={Col} controlId="search">
                          <Form.Control
                            type="text"
                            placeholder="Search..."
                            value={search}
                            onChange={onSearchInputChanged}
                            className="fs-6"
                          />
                        </Form.Group>
                        <Col />
                      </Row>
                    </Form>
                  </th>
                </tr>
                <tr>
                  <th></th>
                  <th>{PATIENT}</th>
                  <th>{KEY_DATES}</th>
                  <th>{PROGRAM}</th>
                  <th className="text-center">{STATUS}</th>
                  <th className="text-center">
                    {ALERT}
                    <button className="btn-outline" onClick={onSortChange}>
                      <i className={`bi bi-${sortTypes[currentState].class}`}></i>
                    </button>
                  </th>
                  <th className="text-center">{LAST_REVIEW}</th>
                  <th className="text-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="reviewAll"
                      checked={review.reviewingPatients.length === props.patients.length}
                      onChange={toggleReviewAll}
                    />
                    <span className="ms-3">{REVIEW_ALL}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...props.patients].sort(sortTypes[currentState].fn).map((patient, i) => {
                  if (
                    search === "" ||
                    patient.givenNames.toString().toLowerCase().includes(search.toLowerCase()) ||
                    patient.familyName.toLowerCase().includes(search.toLowerCase()) ||
                    patient.urn.includes(search) ||
                    patient.birthDate.toString().includes(search)
                  ) {
                    if (
                      (patient.diabetesType === diagnosisType &&
                        managementType === "" &&
                        team === "") ||
                      (patient.managementType &&
                        patient.managementType.includes(managementType) &&
                        diagnosisType === "" &&
                        team === "") ||
                      (patient.team === team && diagnosisType === "" && managementType === "") ||
                      (patient.diabetesType === diagnosisType &&
                        patient.managementType &&
                        patient.managementType.includes(managementType) &&
                        team === "") ||
                      (patient.managementType &&
                        patient.managementType.includes(managementType) &&
                        patient.team === team &&
                        diagnosisType === "") ||
                      (patient.diabetesType === diagnosisType &&
                        patient.team === team &&
                        managementType === "") ||
                      (managementType === "" && diagnosisType === "" && team === "")
                    ) {
                      return (
                        <tr key={i} onClick={(e) => onRowSelected(patient, e)} className="middle">
                          <td className="centre"><Tooltip title="Archive">
                            <IconButton onClick={(e) => setArchivePatient(patient) }>
                              <ArchiveOutlined sx={{color:"#78909C"}} />
                            </IconButton>
                          </Tooltip>
                          </td>
                          <td>
                            <PatientDisplay patient={patient}/>
                          </td>
                          <td>
                            <i className="me-2">{DUE_DATE}:</i>
                            {FormatDateForDisplay(patient.dueDate)}
                            <div className="mt-2">
                              <i className="me-2">{GESTATION_DIABETES}:</i>
                              {patient.diabetesGestation} {WEEK}s
                            </div>
                            <div className="mt-2">
                              <i className="me-2">{CURRENT_GESTATION}:</i>
                              {CalculateCurrentGestation(
                                patient.startDate,
                                patient.currentGestation
                              )}{" "}
                              {WEEK}s
                            </div>
                            <div className="mt-2">
                              <i className="me-2">{DIAGNOSIS_DATE}:</i>
                              {CalculateDiagnosisDate(
                                patient.startDate,
                                patient.currentGestation,
                                patient.diabetesGestation
                              )}
                            </div>
                          </td>
                          <td>
                            <i className="me-2">{DIAGNOSIS}:</i>
                            {patient.diabetesType}
                            <div className="mt-2">
                              <i className="me-2">{MANAGEMENT_TYPE}:</i>
                              {patient.managementType
                                ? patient.managementType.sort().toString()
                                : ""}
                            </div>
                            <div className="mt-2">
                              <i className="me-2">{STARTED}:</i>
                              {FormatDateForDisplay(patient.startDate)}
                            </div>
                            <div className="mt-2">
                              <i className="me-2">{TEAM}:</i>
                              {patient.team}
                            </div>
                            {/* <div className="mt-2">
                              <i className="me-2">{WEEK}:</i>
                              {getProgramWeek(patient.startDate)}
                            </div> */}
                          </td>
                          <td className="text-center">{getStatus(patient)}</td>
                          { showAlerts(patient) ? (
                              <td className="left padded">
                                <div className="pad-left-15">{patient.fastingHigh ? alertRed() : alertGreen()}{" "}{HIGH} {FASTING}</div>  
                                <div className="pad-left-15">{patient.amHigh ? alertRed() : alertGreen()}{" "}{HIGH} {POST_MEAL}</div>
                                <div className="pad-left-15">{patient.bglLow ? alertOrange() : alertGreen()}{" "}{LOW}</div>
                                <div className="pad-left-15">{patient.noData ? alertOrange() : alertGreen()}{" "}{NO_DATA}</div>
                                <div className="pad-left-15">{patient.sysHigh ? alertRed() : alertGreen()}{" "}{HIGH} {SYSTOLIC}</div>
                              </td>
                          ) : (
                            <td className="text-center">
                              Not Tracked
                            </td>
                          )}
                          {/* { showAlerts(patient) ? (
                            <td className="text-center">
                              <h6>{BP}</h6>
                              {SYSTOLIC} BP:{" "}
                              {patient.sysHigh ? alertRed():alertGreen()}
                            </td>
                          ) : (
                            <td className="text-center">
                              Not Tracked
                            </td>
                          )} */}
                          <td className="text-center">
                            {LastReviewedDate(patient.lastReviewDate)}
                          </td>
                          <td className="text-center">
                            <ResourceCheckbox
                              resource={patient}
                              toggleResource={toggleReviewPatient}
                              checked={review.reviewingPatients.findIndex((i: { resourceId: string; }) => i.resourceId===patient.resourceId) > -1}
                              //disabled={patient.documentId === null} //cannot do a review on unlinked
                            />
                          </td>
                        </tr>
                      );
                    }
                  }
                  return <></>;
                })}
              </tbody>
            </Table>
          ) : (
            <div>No Activate Patients</div>
          )}
          <Modal
              show={archivePatient !== undefined}
              backdrop="static"
              keyboard={false}
            >
              <Modal.Header closeButton>
                <Modal.Title>Archive Patient</Modal.Title>
              </Modal.Header>
              <Modal.Body className="fs-6">
                {archivePatient?.givenNames} {archivePatient?.familyName} 
                <div className="mt-2"> Born: {FormatDateForDisplay(archivePatient?.birthDate)} URN: {archivePatient?.urn}</div>
                <Form>
                  <Form.Group as={Col} sm={3} controlId="archivedDate">
                    <Form.FloatingLabel label={DATE} className="text-secondary">
                      <Form.Control
                        type="date"
                        placeholder=""
                        value={archiveOn}
                        onChange={(e) => { setArchiveOn(e.target.value); archivePatient!.archivedOn = e.target.value; }}
                        required />
                    </Form.FloatingLabel> 
                  </Form.Group>
                  <Form.Group controlId="selectValidation">
                    <Form.Select className="mt-3 fs-6" 
                      onChange={(e) => onArchiveSelectChange(e)} isInvalid={isInvalid}>
                      <option>Select</option>
                      <option value="Birth">Birth</option>
                      <option value="Still Birth">Still Birth</option>
                      <option value="Miscarriage">Miscarriage</option>
                      <option value="Withdrawn">Withdrawn</option>
                      <option value="Entered In Error">Entered In Error</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Please select a reason.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={(e) => setArchivePatient(undefined) }>
                  Cancel
                </Button>
                <Button className="fs-6 btn-color" onClick={(e) => onArchiveConfirmClicked()}>Confirm</Button>
              </Modal.Footer>
            </Modal>
        </Card.Body>
      </Card>
      {/* <div>{fetchedData.isError}</div> */}
    </>
  );
};

export default ActivePatients;
